import type {LanguageCode, NamespaceId} from 'components/translation/utils';
import {
  doc,
  getFirestore,
  type DocumentReference,
  collection,
  Timestamp,
  type CollectionReference,
  type FirestoreDataConverter,
} from 'firebase/firestore';
import {
  getCourseTranslationDocumentPath,
  getTranslationCollectionPath,
  getTranslationDocumentPath,
  type CourseTranslationDocument,
  type TranslationNamespaceDocument,
} from './translationDocumentInterfaces';
import type {CourseId} from 'types/common';

const TRANSLATION_NAMESPACE_DOCUMENT_CONVERTER: FirestoreDataConverter<TranslationNamespaceDocument> =
  {
    toFirestore: (data: TranslationNamespaceDocument) => {
      return {
        translations: data,
        lastUpdated: Timestamp.now(),
      };
    },
    fromFirestore: (snapshot, options) => {
      const data = snapshot.data(options);
      if (
        !data ||
        typeof data !== 'object' ||
        !('translations' in data) ||
        typeof data.translations !== 'object'
      ) {
        throw new Error('Invalid data in snapshot:' + JSON.stringify(data));
      }
      return data.translations;
    },
  };

const COURSE_TRANSLATION_DOCUMENT_CONVERTER: FirestoreDataConverter<CourseTranslationDocument> =
  {
    toFirestore: (data: CourseTranslationDocument) => {
      return {
        supportedLanguages: data.supportedLanguages,
        lastUpdated: Timestamp.now(),
      };
    },
    fromFirestore: (snapshot, options) => {
      const data = snapshot.data(options);
      if (
        !data ||
        typeof data !== 'object' ||
        !('supportedLanguages' in data)
      ) {
        throw new Error(
          'Invalid data in snapshot:' +
            (typeof data === 'object' ? JSON.stringify(data) : data),
        );
      }
      return {
        supportedLanguages: data.supportedLanguages,
      };
    },
  };

/**
 * Gets the collection of namespaced translation documents for a course and
 * language.
 */
export function getTranslationCollection({
  courseId,
  language,
}: {
  courseId: CourseId;
  language: LanguageCode;
}): CollectionReference<TranslationNamespaceDocument> {
  return collection(
    getFirestore(),
    getTranslationCollectionPath({
      courseId: courseId || 'unknown',
      language: language || 'en',
    }),
  ).withConverter(TRANSLATION_NAMESPACE_DOCUMENT_CONVERTER);
}

/**
 * Gets a translation document for a course, language, and namespace.
 * @see {@link TranslationNamespaceDocument}
 */
export function getTranslationDocument({
  courseId,
  language,
  namespace,
}: {
  courseId: CourseId;
  language: LanguageCode;
  namespace: NamespaceId;
}): DocumentReference<TranslationNamespaceDocument> {
  return doc(
    getFirestore(),
    getTranslationDocumentPath({
      courseId,
      language,
      namespace: namespace || 'unknown',
    }),
  ).withConverter(TRANSLATION_NAMESPACE_DOCUMENT_CONVERTER);
}

/**
 * Gets metadata about translations available to the course.
 * @see {@link CourseTranslationDocument}
 * @param courseId The course ID to get translations for.
 */
export function getCourseTranslationDocument({
  courseId,
}: {
  courseId: CourseId;
}): DocumentReference<CourseTranslationDocument> {
  return doc(
    getFirestore(),
    getCourseTranslationDocumentPath({courseId}),
  ).withConverter(COURSE_TRANSLATION_DOCUMENT_CONVERTER);
}
